import { graphql } from '@libs/graphql-types';
import { useQuery } from '@apollo/client';

import { apolloClient } from '../index';
import { useEffect, useMemo } from 'react';
import { edgeNodeIterator, useEdgeNodes } from 'shared/utils/graphqlEdges';
import {
  getOrCreateDefaultAccountImageLibrary,
  getOrCreateDefaultBusinessImageLibrary,
} from './mutations';
import { ResultOf } from '@graphql-typed-document-node/core';

const STOCK_IMAGES_FOR_IMAGE_KEYS_QUERY = graphql(/* GraphQL */ `
  query StockImagesForImageKeys($imageKeys: [String!]!) {
    stockImagesForImageKeys(imageKeys: $imageKeys) {
      id
      stockAssetId
      stockSearchId
    }
  }
`);

/**
 * Query fetches all stock images which match an array of image keys (found via ImageAsset.getKey())
 */
export const getStockImagesForImageKeys = async (imageKeys: string[]) => {
  if (!imageKeys || imageKeys.length === 0) {
    return [];
  }

  const { data } = await apolloClient.query({
    query: STOCK_IMAGES_FOR_IMAGE_KEYS_QUERY,
    variables: { imageKeys },
  });

  return data.stockImagesForImageKeys;
};

export const IMAGE_LIBRARY_IMAGE_DATA_FOR_IMAGE_ASSET_KEY_QUERY = graphql(/* GraphQL */ `
  query ImageLibraryImageByImageKey($imageKey: String!) {
    imageLibraryImageByImageKey(imageKey: $imageKey) {
      id
      guid
      baseUrl
      enhancedImages {
        edges {
          node {
            id
            image
            baseUrl
          }
        }
      }
      imageType
      imageWidth
      imageHeight
    }
  }
`);

/**
 * Query fetches a single stock/uploaded image by its key (found via ImageAsset.getKey())
 */
export const useImageLibraryImageDataForImageAssetKey = (imageKey?: string) => {
  const { data, loading } = useQuery(IMAGE_LIBRARY_IMAGE_DATA_FOR_IMAGE_ASSET_KEY_QUERY, {
    variables: {
      imageKey: imageKey ?? '',
    },
    skip: !imageKey,
  });

  return {
    imageLibraryImage: data?.imageLibraryImageByImageKey ?? null,
    isLoading: loading,
  };
};

// Queries for an account's image libraries as well as their account group image libraries if applicable
export const accountImageLibraryQuery = graphql(/* GraphQL */ `
  query AccountImageLibraries($accountGUID: String) {
    accountByGuid(guid: $accountGUID) {
      id
      guid
      displayName

      imageLibraries {
        edges {
          node {
            ...ImageLibraryFragment
          }
        }
      }

      accountGroups {
        edges {
          node {
            id
            displayName
            imageLibraries {
              edges {
                node {
                  ...ImageLibraryFragment
                }
              }
            }
          }
        }
      }
    }
  }
`);

/**
 *
 * Hook fetches and parses image library data for a given account and any of that account's account groups.
 *
 * @param {string} accountGUID    GUID of account to fetch image library data for
 */
export const useAccountAndAccountGroupImageLibraries = (accountGUID: string | null) => {
  const { data: queryData, loading: isLoading } = useQuery(accountImageLibraryQuery, {
    variables: {
      accountGUID,
    },
    skip: !accountGUID,
    // The first time that this hook is run, make sure we call out to the server to ensure
    // our image library data is in sync with what's in the DB
    fetchPolicy: 'network-only',
    // On subsequent re-runs of this hook, prefer to use data in the apollo cache
    nextFetchPolicy: 'cache-first',
  });

  const accountWithImageLibraries = queryData?.accountByGuid;

  return {
    isLoading,
    accountWithImageLibraries,
  };
};

export const accountImageLibrarySlugsQuery = graphql(/* GraphQL */ `
  query AccountImageLibrarySlugs($accountGUID: String) {
    accountByGuid(guid: $accountGUID) {
      id
      guid
      displayName
      imageLibraries {
        edges {
          node {
            ...LightImageLibrary
          }
        }
      }
      accountGroups {
        edges {
          node {
            id
            displayName
            imageLibraries {
              edges {
                node {
                  ...LightImageLibrary
                }
              }
            }
          }
        }
      }
    }
  }
`);

export const useAccountAndAccountGroupImageLibrarySlugs = (accountGUID: string | null) => {
  const { data: queryData, loading: isLoading } = useQuery(accountImageLibrarySlugsQuery, {
    variables: {
      accountGUID,
    },
    skip: !accountGUID,
    // The first time that this hook is run, make sure we call out to the server to ensure
    // our image library data is in sync with what's in the DB
    fetchPolicy: 'network-only',
    // On subsequent re-runs of this hook, prefer to use data in the apollo cache
    nextFetchPolicy: 'cache-first',
  });

  const accountWithImageLibrarySlugs = queryData?.accountByGuid;

  return {
    isLoading,
    accountWithImageLibrarySlugs,
  };
};

// Queries for a business' image libraries
export const businessImageLibraryQuery = graphql(/* GraphQL */ `
  query BusinessImageLibraries($businessGUID: String) {
    businessByGuid(guid: $businessGUID) {
      id
      guid
      totalImageCount
      logoImage {
        id
        guid
        baseUrl
        updatedAt
      }
      businessName

      imageLibraries {
        edges {
          node {
            ...ImageLibraryFragment
          }
        }
      }
    }
  }
`);

/**
 * Hook fetches and parses image library data for a given business
 *
 * @param {string} businessGUID    GUID of account to fetch image library data for
 * @param {bool}  [shouldIncludePlaceholderLibrary=true]  Whether to include a placeholder library if the business doesn't have an existing library
 */
export const useBusinessImageLibraries = (businessGUID: string | null) => {
  const { data: queryData, loading: isLoading } = useQuery(businessImageLibraryQuery, {
    variables: {
      businessGUID: businessGUID,
    },
    skip: !businessGUID,
  });

  const businessWithImageLibraries = queryData?.businessByGuid;

  return {
    isLoading: isLoading,
    businessWithImageLibraries,
  };
};

export const businessImageLibrarySlugsQuery = graphql(/* GraphQL */ `
  query BusinessImageLibrarySlugs($businessGUID: String) {
    businessByGuid(guid: $businessGUID) {
      id
      guid
      businessName
      totalImageCount
      logoImage {
        id
        guid
        baseUrl
        updatedAt
      }
      imageLibraries {
        edges {
          node {
            ...LightImageLibrary
          }
        }
      }
    }
  }
`);

/**
 * Hook fetches the slugs of all image libraries for a given business; any additional image library data
 * can be fetched using the `useImageLibraryBySlug` hook. This allows us to lazily only load image library images
 * when needed.
 */
export const useBusinessImageLibrarySlugs = (businessGUID: string | null) => {
  const { data: queryData, loading: isLoading } = useQuery(businessImageLibrarySlugsQuery, {
    variables: {
      businessGUID,
    },
    skip: !businessGUID,
    // The first time that this hook is run, make sure we call out to the server to ensure
    // our image library data is in sync with what's in the DB
    fetchPolicy: 'network-only',
    // On subsequent re-runs of this hook, prefer to use data in the apollo cache
    nextFetchPolicy: 'cache-first',
  });

  const businessWithImageLibrarySlugs = queryData?.businessByGuid;
  return {
    isLoading,
    businessWithImageLibrarySlugs,
  };
};

/**
 * Gets or creates a default business image library for the given business
 * @param businessGUID - Business GUID to get or create the image library for
 * @param shouldSkipAutoCreation - Whether to skip auto-creation of the image library if it doesn't exist
 */
export const useGetOrCreateDefaultBusinessImageLibrary = (
  businessGUID: string | null,
  shouldSkipAutoCreation = false,
) => {
  const { businessWithImageLibrarySlugs, isLoading } = useBusinessImageLibrarySlugs(businessGUID);

  const defaultBusinessImageLibrary = useEdgeNodes(
    businessWithImageLibrarySlugs?.imageLibraries.edges,
  )[0];

  // If we have business data loaded but there are no image libraries, we need to create one
  const shouldCreateDefaultBusinessImageLibrary =
    !shouldSkipAutoCreation &&
    Boolean(businessGUID) &&
    businessWithImageLibrarySlugs?.guid === businessGUID &&
    !defaultBusinessImageLibrary;

  useEffect(() => {
    if (shouldCreateDefaultBusinessImageLibrary) {
      getOrCreateDefaultBusinessImageLibrary(businessGUID);
    }
  }, [businessGUID, shouldCreateDefaultBusinessImageLibrary]);

  return {
    // Make sure to cast to indicate that this object may be undefined
    defaultBusinessImageLibrary: defaultBusinessImageLibrary as
      | typeof defaultBusinessImageLibrary
      | undefined,
    isLoading: isLoading || shouldCreateDefaultBusinessImageLibrary,
  };
};

/**
 * Gets or creates a default account image library for the given account
 * @param accountGUID - Account GUID to get or create the image library for
 * @param shouldSkipAutoCreation - Whether to skip auto-creation of the image library if it doesn't exist
 */
export const useGetOrCreateDefaultAccountImageLibrary = (
  accountGUID: string | null,
  shouldSkipAutoCreation = false,
) => {
  const { accountWithImageLibrarySlugs, isLoading } =
    useAccountAndAccountGroupImageLibrarySlugs(accountGUID);

  const defaultAccountImageLibraryNode = useEdgeNodes(
    accountWithImageLibrarySlugs?.imageLibraries.edges,
  )[0];

  // If we have account data loaded but there are no image libraries, we need to create one
  const shouldCreateDefaultAccountImageLibrary =
    !shouldSkipAutoCreation &&
    Boolean(accountGUID) &&
    accountWithImageLibrarySlugs?.guid === accountGUID &&
    !defaultAccountImageLibraryNode;

  useEffect(() => {
    if (shouldCreateDefaultAccountImageLibrary) {
      getOrCreateDefaultAccountImageLibrary(accountGUID);
    }
  }, [accountGUID, shouldCreateDefaultAccountImageLibrary]);

  const staticAccountGroupImageLibraries = useMemo(() => {
    const imageLibraries = [];
    for (const accountGroup of edgeNodeIterator(
      accountWithImageLibrarySlugs?.accountGroups.edges,
    )) {
      for (const library of edgeNodeIterator(accountGroup.imageLibraries?.edges)) {
        imageLibraries.push(library);
      }
    }
    return imageLibraries;
  }, [accountWithImageLibrarySlugs?.accountGroups.edges]);

  return {
    defaultAccountImageLibrary: defaultAccountImageLibraryNode as
      | typeof defaultAccountImageLibraryNode
      | undefined,
    staticAccountGroupImageLibraries,
    isLoading: isLoading || shouldCreateDefaultAccountImageLibrary,
  };
};

const imageLibraryBySlugQuery = graphql(/* GraphQL */ `
  query ImageLibraryBySlug($imageLibrarySlug: String, $removedAt_Isnull: Boolean!) {
    imageLibraryBySlug(slug: $imageLibrarySlug) {
      id
      pk
      slug
      displayName
      images(removedAt_Isnull: $removedAt_Isnull) {
        edges {
          node {
            ...ImageLibraryImageFragment
          }
        }
      }
    }
  }
`);

export type ImageLibraryBySlug = NonNullable<
  ResultOf<typeof imageLibraryBySlugQuery>['imageLibraryBySlug']
>;

/**
 * Gets an ImageLibrary by its slug
 * @param {string} imageLibrarySlug    Slug of the image library to fetch
 */
export const useImageLibraryBySlug = (
  imageLibrarySlug: string | null,
  {
    shouldSkip = false,
  }: {
    shouldSkip?: boolean;
  } = {},
) => {
  const { data: queryData, loading: isLoading } = useQuery(imageLibraryBySlugQuery, {
    variables: {
      imageLibrarySlug,
      removedAt_Isnull: true, // Default to only fetching non-removed images
    },
    skip: shouldSkip || !imageLibrarySlug,
  });

  return {
    imageLibrary: queryData?.imageLibraryBySlug ?? null,
    isLoading,
  };
};

export const useImageLibraryRemovedImagesBySlug = (
  imageLibrarySlug: string | null,
  { shouldSkip = false }: { shouldSkip?: boolean } = {},
) => {
  const { data: queryData, loading: isLoading } = useQuery(imageLibraryBySlugQuery, {
    variables: {
      imageLibrarySlug,
      removedAt_Isnull: false, // Only fetch removed images
    },
    skip: shouldSkip || !imageLibrarySlug,
  });

  return {
    imageLibrary: queryData?.imageLibraryBySlug ?? null,
    isLoading,
  };
};
